import styled from 'styled-components';

import { Text } from '../../cdl/Text/Text';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import type { DemandCategorySummary } from '../types/DemandCategorySummary';
import type { DemandProductCategory } from '../types/DemandProductCategory';

const ChartSegment = styled.circle`
    transition:
        stroke 220ms cubic-bezier(0.22, 1, 0.36, 1),
        opacity 220ms ease-in-out;

    &:focus:not(:focus-visible) {
        outline: none;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const HISTORICAL_CATEGORY_SHADES = ['#DCEBF1', '#C8DEE8', '#B3D0DC', '#9EBECD', '#89ACBF', '#749AAC'];
const CHART_SIZE = 160;
const CHART_RADIUS = 56;
const CHART_CIRCUMFERENCE = 2 * Math.PI * CHART_RADIUS;
const SEGMENT_GAP = 2;

interface DemandHistoricalChartProps {
    categories: DemandCategorySummary[];
    categoryLabels: Map<DemandProductCategory, string>;
    totalVolume: number;
    selectedCategory?: DemandProductCategory;
    hoveredCategory?: DemandProductCategory;
    onHoverCategory: (category?: DemandProductCategory) => void;
    onSelectCategory: (category: DemandProductCategory) => void;
}

export const DemandHistoricalChart = ({
    categories,
    categoryLabels,
    totalVolume,
    selectedCategory,
    hoveredCategory,
    onHoverCategory,
    onSelectCategory,
}: DemandHistoricalChartProps) => {
    const activeCategory = hoveredCategory ?? selectedCategory;
    const activeData = categories.find((category) => category.category === activeCategory);
    let usedFraction = 0;

    return (
        <Box width={CHART_SIZE} height={CHART_SIZE} flexShrink={0} position="relative">
            <svg width={CHART_SIZE} height={CHART_SIZE} viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}>
                <circle
                    cx={CHART_SIZE / 2}
                    cy={CHART_SIZE / 2}
                    r={CHART_RADIUS}
                    fill="none"
                    stroke={theme.colors.background.emphasis}
                    strokeWidth={28}
                />
                {categories.map((category, index) => {
                    const fraction = totalVolume > 0 ? category.volume / totalVolume : 0;
                    const offset = usedFraction;
                    const segmentLength = fraction * CHART_CIRCUMFERENCE;
                    const gap = Math.min(SEGMENT_GAP, segmentLength * 0.25);
                    usedFraction += fraction;
                    return (
                        <ChartSegment
                            key={category.category}
                            cx={CHART_SIZE / 2}
                            cy={CHART_SIZE / 2}
                            r={CHART_RADIUS}
                            fill="none"
                            stroke={
                                activeCategory === category.category
                                    ? theme.colors.accent.foreground
                                    : HISTORICAL_CATEGORY_SHADES[Math.min(index, HISTORICAL_CATEGORY_SHADES.length - 1)]
                            }
                            strokeWidth={28}
                            opacity={hoveredCategory && hoveredCategory !== category.category ? 0.88 : 1}
                            strokeDasharray={`${Math.max(0, segmentLength - gap)} ${CHART_CIRCUMFERENCE}`}
                            strokeDashoffset={-(offset * CHART_CIRCUMFERENCE + gap / 2)}
                            transform={`rotate(-90 ${CHART_SIZE / 2} ${CHART_SIZE / 2})`}
                            style={{ cursor: 'pointer' }}
                            aria-pressed={selectedCategory === category.category}
                            role="button"
                            tabIndex={0}
                            aria-label={`${categoryLabels.get(category.category) ?? category.category}: ${category.sharePercent.toFixed(1)}%`}
                            onMouseEnter={() => onHoverCategory(category.category)}
                            onMouseLeave={() => onHoverCategory(undefined)}
                            onFocus={() => onHoverCategory(category.category)}
                            onBlur={() => onHoverCategory(undefined)}
                            onClick={() => onSelectCategory(category.category)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onSelectCategory(category.category);
                                }
                            }}
                        />
                    );
                })}
            </svg>
            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{ pointerEvents: 'none' }}
            >
                {activeData ? (
                    <Text variant="small" color="foreground.onEmphasis" fontWeight={600}>
                        {formatNumber({
                            number: activeData.sharePercent,
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                        })}
                        %
                    </Text>
                ) : null}
            </Box>
        </Box>
    );
};
